import { Route, Routes, useParams } from "react-router-dom-v5-compat";
import { Role, useAthleteNavQuery, useMyAxleDataForNavbarQuery } from "../../../../types";
import { useNutritionFlags } from "../../../../hooks/useNutritionFlags";
import { parseAxleAppAccess } from "../../PrimaryNavUtils";
import { RoleRoute } from "../../ProtectedRoutes";
import { NavLink } from "../../SecondaryNav/BreadCrumbs";
import { SecondaryNavLayout, SecondaryNavLayoutProps } from "../../SecondaryNav/SecondaryNavLayout";
import { getNavOrgTeamLink } from "../Team/TeamPaths";
import { teamManagementLink, teamsLink } from "../TeamManagement/TeamManagementPaths";
import { AthleteAnthropometryPage } from "./AthleteAnthropometryPage";
import { AthleteFoodLogsPage } from "./AthleteFoodLogsPage";
import { AthleteGoalCompliancePage } from "./AthleteGoalCompliancePage";
import { AthleteGoalsPage } from "./AthleteGoalsPage";
import { AthleteMealPlansPage } from "./AthleteMealPlansPage";
import { AthleteNotesPage } from "./AthleteNotesPage";
import {
  NAV_ATHLETE_ANTHROPOMETRY,
  NAV_ATHLETE_FOOD_LOGS,
  NAV_ATHLETE_GOALS,
  NAV_ATHLETE_MEAL_PLANS,
  NAV_ATHLETE_NOTES,
  NAV_ATHLETE_SUMMARY,
  NAV_ATHLETE_ENERGY_EXPENDITURE,
  getNavOrgAthleteLinkWithTeam,
} from "./AthletePaths";
import { AthleteSummaryPage } from "./AthleteSummaryPage";
import { AthleteEnergyExpenditurePage } from "./AthleteEnergyExpenditurePage";
import { Box, Typography } from "@mui/material";

const getSecondaryNavLayoutProps = (
  current: NavLink,
  teamLink: NavLink,
  isEnergyExpenditureEnabled: boolean,
  hasAMS: boolean,
  hasAmsEnergyExpenditureIntegration: boolean
): SecondaryNavLayoutProps => ({
  name: "Athlete",
  current,
  breadCrumbs: [teamManagementLink, teamsLink, teamLink],
  enableAthleteSearch: true,
  tabs: [
    {
      name: "Summary",
      to: NAV_ATHLETE_SUMMARY,
    },
    {
      name: "Meal Plans",
      to: NAV_ATHLETE_MEAL_PLANS,
      allowedRoles: ["dietitian"],
    },
    {
      name: "Goals",
      to: NAV_ATHLETE_GOALS,
    },
    {
      name: "Food Logs",
      to: NAV_ATHLETE_FOOD_LOGS,
    },
    ...(isEnergyExpenditureEnabled && hasAMS && hasAmsEnergyExpenditureIntegration
      ? [
          {
            name: "Energy Expenditure",
            to: NAV_ATHLETE_ENERGY_EXPENDITURE,
            allowedRoles: ["dietitian", "nonRdAdmin"] as Role[],
          },
        ]
      : []),
    {
      name: "Anthropometry",
      to: NAV_ATHLETE_ANTHROPOMETRY,
    },
    {
      name: "Notes",
      to: NAV_ATHLETE_NOTES,
    },
  ],
});

export const AthleteRouter = () => {
  const { athleteId: maybeAthleteId, teamId: maybeTeamId } = useParams();
  const athleteId = maybeAthleteId || "";
  const teamId = maybeTeamId || "";
  const { isEnergyExpenditureEnabled } = useNutritionFlags();
  const { data: axleData } = useMyAxleDataForNavbarQuery({ fetchPolicy: "cache-only" });
  const { hasAMS } = parseAxleAppAccess(axleData?.myAxleDataForNavbar?.appAccess);
  let athleteName = "";
  let teamName = "";
  const { data, loading } = useAthleteNavQuery({ variables: { athleteId, teamId } });
  const hasAmsEnergyExpenditureIntegration = data?.myOrg.hasAmsEnergyExpenditureIntegration ?? false;

  if (!loading && data) {
    const {
      athlete: { firstName, lastName },
      team: { name },
    } = data;

    athleteName = `${lastName}, ${firstName}`;
    teamName = name;
  }

  const teamLink = getNavOrgTeamLink(teamId, teamName);
  const current = getNavOrgAthleteLinkWithTeam(athleteId, athleteName, teamId);
  const secondaryNavLayoutProps = getSecondaryNavLayoutProps(
    current,
    teamLink,
    isEnergyExpenditureEnabled,
    hasAMS,
    hasAmsEnergyExpenditureIntegration
  );

  return (
    <Routes>
      <Route element={athleteName && <SecondaryNavLayout {...secondaryNavLayoutProps} />}>
        <Route element={<RoleRoute roles={["dietitian", "nonRdAdmin"]} />}>
          <Route index element={<AthleteSummaryPage />} />
          <Route path={NAV_ATHLETE_SUMMARY} element={<AthleteSummaryPage />} />
          <Route path={NAV_ATHLETE_MEAL_PLANS} element={<AthleteMealPlansPage />} />
          <Route path={`${NAV_ATHLETE_MEAL_PLANS}/:mealPlanId?`} element={<AthleteMealPlansPage />} />
          <Route path={NAV_ATHLETE_FOOD_LOGS} element={<AthleteFoodLogsPage />} />
          <Route path={NAV_ATHLETE_ANTHROPOMETRY} element={<AthleteAnthropometryPage />} />
          <Route path={NAV_ATHLETE_NOTES} element={<AthleteNotesPage />} />
          <Route path={NAV_ATHLETE_GOALS} element={<AthleteGoalsPage />} />
          {isEnergyExpenditureEnabled && hasAMS && hasAmsEnergyExpenditureIntegration && (
            <Route path={NAV_ATHLETE_ENERGY_EXPENDITURE} element={<AthleteEnergyExpenditurePage />} />
          )}
        </Route>
      </Route>
      <Route path={`${NAV_ATHLETE_GOALS}/:goalConfigurationId`} element={<AthleteGoalCompliancePage />} />
    </Routes>
  );
};
